import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

import Breadcrumbs from "../components/Breadcrumbs";
import Button from "../components/Button";
import api from "../api/api";

const EditWarehouseRequest = () => {
    const navigate = useNavigate();
    const { id } = useParams();

    const [loading, setLoading] = useState(true);
    const [bookingType, setBookingType] = useState("");
    const [fileName, setFileName] = useState("");
    const [file, setFile] = useState(null);

    // Full Day
    const [fullStartDate, setFullStartDate] = useState(null);
    const [fullEndDate, setFullEndDate] = useState(null);

    // Hourly
    const [hourlyDate, setHourlyDate] = useState(null);
    const [hourStartTime, setHourStartTime] = useState(null);
    const [hourEndTime, setHourEndTime] = useState(null);

    const [selectedWarehouses, setSelectedWarehouses] = useState([]);

    const [formValues, setFormValues] = useState({
        pincode: "",
        productName: "",
        hsnCode: "",
        storageType: "",
        packagingType: "",
        totalUnits: "",
        unitsType: "",
        tareWeight: "",
        length: "",
        breadth: "",
        height: "",
        volumetricWeight: "",
        productDescription: "",
        sku: "",
        specialRequirement: "",
        comment: "",
        hourlyType: "",
    });

    // Handle booking type change
    const handleBookingTypeChange = (type) => {
        setBookingType(type);

        // Clear irrelevant date/time values when switching types
        if (type === "fullDay") {
            // Clear hourly values when switching to full day
            setHourlyDate(null);
            setHourStartTime(null);
            setHourEndTime(null);
            setFormValues(prev => ({ ...prev, hourlyType: "" }));
        } else if (type === "hourly") {
            // Clear full day values when switching to hourly
            setFullStartDate(null);
            setFullEndDate(null);
        }
    };

    // ---------------- FETCH DATA ----------------

    useEffect(() => {
        fetchRequestDetails();
    }, []);

    const fetchRequestDetails = async () => {
        try {
            const response = await api.get(`/warehouse/request-details/${id}`);
            const { request, documents } = response.data;

            setFormValues({
                pincode: request.pincode || "",
                productName: request.product_name || "",
                hsnCode: request.HSN_code || "",
                storageType: request.storage_type || "",
                packagingType: request.packaging_type || "",
                totalUnits: request.total_unit || "",
                unitsType: request.unit_type || "",
                tareWeight: request.dead_weight || "",
                length: request.length || "",
                breadth: request.Breadth || "",
                height: request.Height || "",
                volumetricWeight: request.volumetric_weight || "",
                productDescription: request.product_information || "",
                sku: request.sku_or_item_code || "",
                specialRequirement: request.special_requirements || "",
                comment: request.comments || "",
                hourlyType: request.hours || "",
            });

            // Warehouse List
            if (Array.isArray(request.selected_warehouses)) {
                setSelectedWarehouses(request.selected_warehouses);
            } else {
                try {
                    const warehouseNames = JSON.parse(request.warehouse_names || "[]");

                    setSelectedWarehouses(
                        warehouseNames.map((name) => ({
                            name,
                        }))
                    );
                } catch {
                    setSelectedWarehouses([]);
                }
            }

            // Set date/time values FIRST based on booking duration
            // Full Day
            if (request.booking_duration === "fullDay") {
                setFullStartDate(
                    request.start_date ? new Date(request.start_date) : null
                );
                setFullEndDate(
                    request.end_date ? new Date(request.end_date) : null
                );
            }

            // Hourly
            if (request.booking_duration === "hourly") {
                setHourlyDate(
                    request.start_date ? new Date(request.start_date) : null
                );

                if (request.start_time) {
                    const start = new Date();
                    const [h, m, s] = request.start_time.split(":");
                    start.setHours(h, m, s);
                    setHourStartTime(start);
                }

                if (request.end_time) {
                    const end = new Date();
                    const [h, m, s] = request.end_time.split(":");
                    end.setHours(h, m, s);
                    setHourEndTime(end);
                }
            }

            // Then set booking type and trigger UI update
            setBookingType(request.booking_duration || "");
            // Trigger UI update based on booking type
            handleBookingTypeChange(request.booking_duration || "");

            setLoading(false);
        } catch (error) {
            console.error("Error fetching request details:", error);
            setLoading(false);
        }
    };

    // ---------------- UPDATE ----------------

    const handleUpdate = async () => {
        try {
            const payload = {
                start_date:
                    bookingType === "fullDay"
                        ? fullStartDate?.toISOString().split("T")[0]
                        : hourlyDate?.toISOString().split("T")[0],

                end_date:
                    bookingType === "fullDay"
                        ? fullEndDate?.toISOString().split("T")[0]
                        : "",

                start_time:
                    bookingType === "hourly"
                        ? hourStartTime?.toTimeString().split(" ")[0]
                        : "",

                end_time:
                    bookingType === "hourly"
                        ? hourEndTime?.toTimeString().split(" ")[0]
                        : "",

                hours: formValues.hourlyType,
                booking_duration: bookingType
            };

            const response = await api.put(`/warehouse/update-request/${id}`, payload);
            alert(response.data.message); // Show backend message about re-confirmation

            navigate("/warehouse-requests");
        } catch (error) {
            console.error("Update Error:", error.response?.data || error.message);
            alert(error.response?.data?.message || error.message || "Failed to update request");
        }
    };

    if (loading) {
        return (
            <div className="text-center mt-5">
                Loading...
            </div>
        );
    }

    return (
        <>
            <Breadcrumbs />

            {/* ---------------- SELECTED WAREHOUSES ---------------- */}
            <h4 className="section-title mb-3 fs-5 fw-semibold">
                Selected Warehouses
            </h4>

            <div className="card shadow-sm border-0 mb-4">
                <div className="card-body">
                    <div className="row g-3 mb-4">
                        <div className="col-md-4">
                            {/* <label className="form-label fw-semibold">
                                Pincode
                            </label> */}
                            {/* <input
                                type="text"
                                className="form-control"
                                value={formValues.pincode}
                                readOnly
                            /> */}
                        </div>
                    </div>
                    <div className="table-responsive">
                        <table className="table table-bordered">
                            <thead>
                                <tr>
                                    <th>Warehouse Name</th>
                                    <th>City</th>
                                    <th>Base Rate</th>
                                </tr>
                            </thead>
                            <tbody>
                                {selectedWarehouses.length > 0 ? (
                                    selectedWarehouses.map((wh, index) => (
                                        <tr key={index}>
                                            <td>{wh.name}</td>
                                            <td>{wh.location_city || "-"}</td>
                                            <td>{wh.base_rate || "-"}</td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="3" className="text-center">
                                            No warehouses selected
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            {/* ---------------- BOOKING SECTION ---------------- */}
            <h4 className="section-title mb-3 fs-5 fw-semibold">
                Edit Booking Duration
            </h4>

            {/* Booking Type Selector */}
            <div className="row g-3 mb-4">
                <div className="col-md-12">
                    <h6 className="mb-3">Select Booking Type</h6>
                    <div className="form-check form-check-inline">
                        <input
                            className="form-check-input"
                            type="radio"
                            name="bookingType"
                            id="fullDayBooking"
                            value="fullDay"
                            checked={bookingType === "fullDay"}
                            onChange={(e) => handleBookingTypeChange(e.target.value)}
                        />
                        <label className="form-check-label" htmlFor="fullDayBooking">
                            Full Day Booking
                        </label>
                    </div>
                    <div className="form-check form-check-inline ms-3">
                        <input
                            className="form-check-input"
                            type="radio"
                            name="bookingType"
                            id="hourlyBooking"
                            value="hourly"
                            checked={bookingType === "hourly"}
                            onChange={(e) => handleBookingTypeChange(e.target.value)}
                        />
                        <label className="form-check-label" htmlFor="hourlyBooking">
                            Hourly Booking
                        </label>
                    </div>
                </div>
            </div>

            {bookingType === "fullDay" && (
                <div className="row g-3">
                    <div className="col-sm-6 col-md-4">
                        <label className="form-label fw-semibold">
                            Start Date
                        </label>
                        <DatePicker
                            selected={fullStartDate}
                            onChange={(date) => setFullStartDate(date)}
                            className="form-control"
                            dateFormat="dd/MM/yyyy"
                            minDate={new Date()}
                        />
                    </div>
                    <div className="col-sm-6 col-md-4">
                        <label className="form-label fw-semibold">
                            End Date
                        </label>
                        <DatePicker
                            selected={fullEndDate}
                            onChange={(date) => setFullEndDate(date)}
                            className="form-control"
                            dateFormat="dd/MM/yyyy"
                            minDate={fullStartDate || new Date()}
                        />
                    </div>
                </div>
            )}

            {bookingType === "hourly" && (
                <div className="row g-3">
                    <div className="col-sm-6 col-md-4">
                        <label className="form-label fw-semibold ">
                            Date
                        </label>
                        <DatePicker
                            selected={hourlyDate}
                            onChange={(date) => setHourlyDate(date)}
                            dateFormat="dd/MM/yyyy"
                            className="form-control"
                            placeholderText="Select Date"
                            minDate={new Date()}
                        />
                    </div>
                    <div className="col-sm-6 col-md-4">
                        <label className="form-label fw-semibold ">
                            Start Time
                        </label>
                        <DatePicker
                            selected={hourStartTime}
                            onChange={(time) => setHourStartTime(time)}
                            showTimeSelect
                            showTimeSelectOnly
                            timeIntervals={15}
                            timeCaption="Start Time"
                            dateFormat="hh:mm aa"
                            className="form-control"
                            placeholderText="Select Start Time"
                        />
                    </div>
                    <div className="col-sm-6 col-md-4">
                        <label className="form-label fw-semibold ">
                            End Time
                        </label>
                        <DatePicker
                            selected={hourEndTime}
                            onChange={(time) => setHourEndTime(time)}
                            showTimeSelect
                            showTimeSelectOnly
                            timeIntervals={15}
                            timeCaption="End Time"
                            dateFormat="hh:mm aa"
                            className="form-control"
                            placeholderText="Select End Time"
                        />
                    </div>
                    <div className="col-sm-6 col-md-4">
                        <label className="form-label fw-semibold">
                            Hours
                        </label>
                        <input
                            type="text"
                            className="form-control"
                            value={formValues.hourlyType}
                            readOnly
                        />
                    </div>
                </div>
            )}

            {/* ---------------- STORAGE REQUIREMENT ---------------- */}
            <h4 className="section-title mb-3 fs-5 fw-semibold">
                Storage Requirement
            </h4>

            <div className="card shadow-sm border-0 mb-4">
                <div className="card-body">
                    <div className="row g-3">
                        <div className="col-md-4">
                            <label className="form-label">
                                Product Name
                            </label>
                            <input
                                type="text"
                                className="form-control"
                                value={formValues.productName}
                                readOnly
                            />
                        </div>
                        <div className="col-md-4">
                            <label className="form-label">
                                HSN Code
                            </label>
                            <input
                                type="text"
                                className="form-control"
                                value={formValues.hsnCode}
                                readOnly
                            />
                        </div>
                        <div className="col-md-4">
                            <label className="form-label">
                                Storage Type
                            </label>
                            <input
                                type="text"
                                className="form-control"
                                value={formValues.storageType}
                                readOnly
                            />
                        </div>
                        <div className="col-md-4">
                            <label className="form-label">
                                Packaging Type
                            </label>
                            <input
                                type="text"
                                className="form-control"
                                value={formValues.packagingType}
                                readOnly
                            />
                        </div>
                        <div className="col-md-4">
                            <label className="form-label">
                                Total Units
                            </label>
                            <input
                                type="text"
                                className="form-control"
                                value={formValues.totalUnits}
                                readOnly
                            />
                        </div>
                        <div className="col-md-4">
                            <label className="form-label">
                                Units Type
                            </label>
                            <input
                                type="text"
                                className="form-control"
                                value={formValues.unitsType}
                                readOnly
                            />
                        </div>
                        <div className="col-md-4">
                            <label className="form-label">
                                Tare Weight
                            </label>
                            <input
                                type="text"
                                className="form-control"
                                value={formValues.tareWeight}
                                readOnly
                            />
                        </div>
                        <div className="col-md-4">
                            <label className="form-label">
                                Length
                            </label>
                            <input
                                type="text"
                                className="form-control"
                                value={formValues.length}
                                readOnly
                            />
                        </div>
                        <div className="col-md-4">
                            <label className="form-label">
                                Breadth
                            </label>
                            <input
                                type="text"
                                className="form-control"
                                value={formValues.breadth}
                                readOnly
                            />
                        </div>
                        <div className="col-md-4">
                            <label className="form-label">
                                Height
                            </label>
                            <input
                                type="text"
                                className="form-control"
                                value={formValues.height}
                                readOnly
                            />
                        </div>
                        <div className="col-md-4">
                            <label className="form-label">
                                Volumetric Weight
                            </label>
                            <input
                                type="text"
                                className="form-control"
                                value={formValues.volumetricWeight}
                                readOnly
                            />
                        </div>
                        <div className="col-md-4">
                            <label className="form-label">
                                SKU / Item Code
                            </label>
                            <input
                                type="text"
                                className="form-control"
                                value={formValues.sku}
                                readOnly
                            />
                        </div>
                        <div className="col-md-6">
                            <label className="form-label">
                                Product Description
                            </label>
                            <textarea
                                className="form-control"
                                value={formValues.productDescription}
                                readOnly
                                rows={3}
                            />
                        </div>
                        <div className="col-md-6">
                            <label className="form-label">
                                Special Requirement
                            </label>
                            <textarea
                                className="form-control"
                                value={formValues.specialRequirement}
                                readOnly
                                rows={3}
                            />
                        </div>
                    </div>
                    <h4 className="section-title mb-3 fs-5 fw-semibold">
                        Additional Information
                    </h4>
                    <div className="card shadow-sm mb-4 border-0">
                        <div className="card-body">
                            {/* Comment (Read Only) */}
                            <div className="mb-3">
                                <label className="form-label fw-semibold">Comment</label>
                                <textarea
                                    className="form-control"
                                    value={formValues.comment}
                                    readOnly
                                    rows={3}
                                />
                                <div className="text-end small text-muted">
                                    {formValues.comment?.length || 0}/500
                                </div>
                            </div>
                            {/* File Section (Read Only) */}
                            <div className="mb-3">
                                <label className="form-label fw-semibold">
                                    Product Certifications
                                </label>
                                {fileName ? (
                                    <div className="d-flex align-items-center gap-2">
                                        <span
                                            className="text-success small text-decoration-underline"
                                        >
                                            {fileName}
                                        </span>
                                        {/* no delete icon in read-only */}
                                    </div>
                                ) : (
                                    <span className="text-muted small">
                                        No file uploaded
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* ---------------- UPDATE BUTTON ---------------- */}
            <div className="d-flex justify-content-end">
                <Button
                    text="Update"
                    onClick={handleUpdate}
                />
            </div>
        </>
    );
};

export default EditWarehouseRequest;